// Panel "Utilisateurs" de l'interface admin.
// Liste paginée + recherche + filtre par statut d'abonnement.
// Actions par ligne : attribuer une licence (Starter/Pro, mensuel/annuel) ou révoquer.
import React, { useEffect, useState, useCallback } from "react";
import {
	adminListUsers,
	adminGrantLicense,
	adminRevokeLicense,
	adminUserBan,
	adminUserUnban,
} from "../services/authService";
import AdminUserDetailModal from "./AdminUserDetailModal";

const STATUS_LABELS = {
	all: "Tous",
	free: "Gratuit",
	starter: "Starter",
	pro: "Pro",
	expired: "Expiré",
};

const TIER_BADGE = {
	free: { label: "Gratuit", color: "var(--ink-soft)" },
	starter: { label: "Starter", color: "#F87B1B" },
	pro: { label: "Pro", color: "#a78bfa" },
};

function formatDate(v) {
	if (!v) return "—";
	const d = new Date(v);
	if (isNaN(d)) return "—";
	return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

// ─── Modale d'attribution de licence ───
function GrantModal({ user, onClose, onGranted }) {
	const [tier, setTier] = useState("pro");
	const [dureeMois, setDureeMois] = useState(12);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	const handleGrant = async () => {
		setError("");
		setSaving(true);
		const result = await adminGrantLicense(user.id, tier, dureeMois);
		setSaving(false);
		if (result.success) {
			onGranted?.();
		} else {
			setError(result.error || "Erreur");
		}
	};

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			<div className="mens-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
				<div className="mens-modal-header">
					<h3>Attribuer une licence</h3>
					<button className="mens-modal-close" onClick={onClose}>×</button>
				</div>

				<p style={{ margin: 0, color: "var(--ink-soft)", fontSize: 14 }}>
					Utilisateur : <strong style={{ color: "var(--ink)" }}>{user.prenom} {user.nom}</strong>
					<br />
					<span style={{ fontSize: 12 }}>{user.email}</span>
				</p>

				<div className="group-settings-section" style={{ marginTop: 18 }}>
					<span className="group-settings-label">Plan</span>
					<div style={{ display: "flex", gap: 8, marginTop: 6 }}>
						{["starter", "pro"].map((t) => (
							<button
								key={t}
								className={`admin-tier-choice ${tier === t ? "selected" : ""}`}
								onClick={() => setTier(t)}
								type="button"
							>
								{TIER_BADGE[t].label}
							</button>
						))}
					</div>
				</div>

				<div className="group-settings-section">
					<span className="group-settings-label">Durée</span>
					<div style={{ display: "flex", gap: 8, marginTop: 6 }}>
						<button
							className={`admin-tier-choice ${dureeMois === 1 ? "selected" : ""}`}
							onClick={() => setDureeMois(1)}
							type="button"
						>
							1 mois
						</button>
						<button
							className={`admin-tier-choice ${dureeMois === 12 ? "selected" : ""}`}
							onClick={() => setDureeMois(12)}
							type="button"
						>
							12 mois
						</button>
					</div>
				</div>

				<div style={{ background: "rgba(248,123,27,0.08)", border: "1px solid rgba(248,123,27,0.25)", borderRadius: 10, padding: 10, fontSize: 12, color: "var(--ink-soft)", marginTop: 14 }}>
					La licence sera marquée <strong>PaymentSource = 'manual'</strong> et n'entraînera aucune facturation.
					Tout abonnement actif de l'utilisateur sera annulé au préalable.
				</div>

				{error && <div className="mens-modal-error">{error}</div>}

				<div className="mens-modal-actions">
					<button className="mens-btn ghost" onClick={onClose} disabled={saving}>Annuler</button>
					<button className="mens-btn primary" onClick={handleGrant} disabled={saving}>
						{saving ? "Attribution..." : "Attribuer la licence"}
					</button>
				</div>
			</div>
		</div>
	);
}

// ─── Panel principal ───
export default function AdminUsersPanel() {
	const [users, setUsers] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [pageSize] = useState(25);
	const [statusFilter, setStatusFilter] = useState("all");
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const [grantTarget, setGrantTarget] = useState(null);
	const [detailUserId, setDetailUserId] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError("");
		const result = await adminListUsers({ page, pageSize, statusFilter, search });
		setLoading(false);
		if (result.success) {
			setUsers(result.users || []);
			setTotal(result.total || 0);
			setTotalPages(result.totalPages || 1);
		} else {
			setError(result.error || "Erreur");
		}
	}, [page, pageSize, statusFilter, search]);

	useEffect(() => { load(); }, [load]);

	const handleSearchSubmit = (e) => {
		e.preventDefault();
		setPage(1);
		setSearch(searchInput.trim());
	};

	const handleFilterChange = (v) => {
		setPage(1);
		setStatusFilter(v);
	};

	const handleRevoke = async (u) => {
		if (!window.confirm(`Révoquer l'abonnement de ${u.prenom} ${u.nom} ?`)) return;
		const result = await adminRevokeLicense(u.id);
		if (result.success) {
			load();
		} else {
			alert(result.error || "Erreur");
		}
	};

	const handleBan = async (u) => {
		const reason = window.prompt(
			`Bannir ${u.prenom} ${u.nom} (${u.email}) ?\n\nMotif (optionnel) :`,
			""
		);
		if (reason === null) return; // Annulé
		const result = await adminUserBan(u.id, reason || "");
		if (result.success) load();
		else alert(result.error || "Erreur");
	};

	const handleUnban = async (u) => {
		if (!window.confirm(`Débannir ${u.prenom} ${u.nom} ?`)) return;
		const result = await adminUserUnban(u.id);
		if (result.success) load();
		else alert(result.error || "Erreur");
	};

	return (
		<div>
			{/* Filtres */}
			<div className="admin-users-toolbar">
				<form onSubmit={handleSearchSubmit} className="admin-users-search">
					<input
						type="text"
						placeholder="Rechercher email, prénom, nom..."
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
					/>
					<button className="mens-btn ghost small" type="submit">Rechercher</button>
					{search && (
						<button
							className="mens-btn ghost small"
							type="button"
							onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}
						>
							Effacer
						</button>
					)}
				</form>

				<div className="admin-users-filters">
					{Object.entries(STATUS_LABELS).map(([key, label]) => (
						<button
							key={key}
							className={`admin-filter-chip ${statusFilter === key ? "selected" : ""}`}
							onClick={() => handleFilterChange(key)}
							type="button"
						>
							{label}
						</button>
					))}
				</div>
			</div>

			{error && <div className="admin-error">{error}</div>}
			{loading && <div className="admin-loading">Chargement des utilisateurs...</div>}

			{/* Tableau */}
			{!loading && users.length === 0 && (
				<div className="admin-empty">Aucun utilisateur ne correspond aux critères.</div>
			)}

			{!loading && users.length > 0 && (
				<div className="admin-users-table-wrap">
					<table className="admin-users-table">
						<thead>
							<tr>
								<th>Utilisateur</th>
								<th>Inscription</th>
								<th>Plan actif</th>
								<th>Source</th>
								<th>Fin d'abo</th>
								<th style={{ textAlign: "right" }}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{users.map((u) => {
								const badge = TIER_BADGE[u.tier] || TIER_BADGE.free;
								const hasActive = u.tier !== "free";
								return (
									<tr
										key={u.id}
										className={`clickable-row ${u.banned ? "row-banned" : ""}`}
										onClick={() => setDetailUserId(u.id)}
									>
										<td>
											<div className="admin-user-name">
												{u.prenom} {u.nom}
												{u.banned && (
													<span className="admin-ban-tag" style={{ marginLeft: 8 }}>BAN</span>
												)}
											</div>
											<div className="admin-user-email">{u.email}</div>
										</td>
										<td>{formatDate(u.creationLog)}</td>
										<td>
											<span className="admin-tier-badge" style={{ color: badge.color, borderColor: badge.color + "80" }}>
												{badge.label}
											</span>
											{u.planLibelle && u.tier !== "free" && (
												<div style={{ fontSize: 11, color: "var(--ink-soft)", marginTop: 2 }}>
													{u.planLibelle}
												</div>
											)}
										</td>
										<td>
											{u.paymentSource ? (
												<span className={`admin-source-tag source-${u.paymentSource}`}>
													{u.paymentSource}
												</span>
											) : "—"}
										</td>
										<td>{formatDate(u.dateFin)}</td>
										<td
											style={{ textAlign: "right", whiteSpace: "nowrap" }}
											onClick={(e) => e.stopPropagation()}
										>
											<button
												className="mens-btn primary small"
												onClick={() => setGrantTarget(u)}
											>
												Attribuer
											</button>
											{hasActive && (
												<button
													className="mens-btn danger small"
													onClick={() => handleRevoke(u)}
													style={{ marginLeft: 6 }}
												>
													Révoquer
												</button>
											)}
											{u.banned ? (
												<button
													className="mens-btn ghost small"
													onClick={() => handleUnban(u)}
													style={{ marginLeft: 6 }}
												>
													Débannir
												</button>
											) : (
												<button
													className="mens-btn danger small"
													onClick={() => handleBan(u)}
													style={{ marginLeft: 6 }}
												>
													Bannir
												</button>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>

					<div className="admin-users-pagination">
						<span>
							{total} utilisateur{total > 1 ? "s" : ""} · Page {page} / {totalPages}
						</span>
						<div>
							<button
								className="mens-btn ghost small"
								disabled={page <= 1}
								onClick={() => setPage(page - 1)}
							>
								← Précédent
							</button>
							<button
								className="mens-btn ghost small"
								disabled={page >= totalPages}
								onClick={() => setPage(page + 1)}
								style={{ marginLeft: 6 }}
							>
								Suivant →
							</button>
						</div>
					</div>
				</div>
			)}

			{grantTarget && (
				<GrantModal
					user={grantTarget}
					onClose={() => setGrantTarget(null)}
					onGranted={() => { setGrantTarget(null); load(); }}
				/>
			)}

			{detailUserId != null && (
				<AdminUserDetailModal
					userId={detailUserId}
					onClose={() => setDetailUserId(null)}
				/>
			)}
		</div>
	);
}
